import { isLegacyWrapper, isNative } from './nativeBridge';

/**
 * Which shell a page load gets: the new mobile app (src/mobile) or the old
 * HomeScreen.
 *
 * The new one is off in a browser unless VITE_NEW_SHELL_ENABLED=true is set
 * for the deployment, which it is from the release that brought permissions
 * and plans (the owner's decision, 2026-10-29); unset is the way back. A wrapper
 * that speaks bridge v2 always gets it, since those builds dropped their
 * native settings screen and only the new shell has one; they are not
 * released until it turns on. A wrapper built before v2 keeps the old shell: its native settings screen and
 * its v1 globals are what that shell talks to, and it has no way to reach
 * the new one's settings. Those builds go when the server's minimum version
 * passes them (docs/native-bridge.md, the plan's step C3).
 *
 * `relay:newShell` = '1' turns it on anywhere, for trying it inside an old
 * build. `?newShell=1` sets that flag and `?newShell=0` clears it, so the
 * choice survives the first navigation, which drops the query.
 */
export const NEW_SHELL_KEY = 'relay:newShell';

/** Storage can throw in a private window or with site data blocked. */
function readFlag(): boolean {
  try {
    return window.localStorage.getItem(NEW_SHELL_KEY) === '1';
  } catch {
    return false;
  }
}

function writeFlag(on: boolean) {
  try {
    if (on) window.localStorage.setItem(NEW_SHELL_KEY, '1');
    else window.localStorage.removeItem(NEW_SHELL_KEY);
  } catch {
    // The query still decides this page load; it only will not be remembered.
  }
}

/** The query's say, if it has one: true, false, or null for nothing. */
function fromQuery(search: string): boolean | null {
  const value = new URLSearchParams(search).get('newShell');
  if (value === '1') return true;
  if (value === '0') return false;
  return null;
}

/** The deployment's switch for browsers. */
export const newShellEnabled =
  import.meta.env.VITE_NEW_SHELL_ENABLED === 'true';

export function shouldUseNewShell(
  search: string = typeof window === 'undefined' ? '' : window.location.search,
  enabled: boolean = newShellEnabled
): boolean {
  const asked = fromQuery(search);
  if (asked !== null) writeFlag(asked);
  if (asked === true || readFlag()) return true;
  if (asked === false) return false;
  if (isNative()) return true;
  return enabled && !isLegacyWrapper();
}
